/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Fragment, useEffect, createElement, useReducer, useState } from 'react';
import useBuilderCommands from 'comps/ResourceBuilder/useCommands';
import * as I from 'immutable';
import Present from 'lib/Present';
import Struct from 'lib/proforma/templates/Struct';
import { Fields } from 'lib/proforma';
import isPath from 'lib/isPath';
import isEmbedded from 'lib/isEmbedded';
import useCachedQuery from 'lib/useCachedQuery';
import GetItemViewResource from './queries/GetItemViewResource';
import getTypeConfig from './getTypeConfig';
import mkPoint from 'lib/mkPoint';
import throttle from 'lib/throttle';
import isSmallScreen from 'lib/isSmallScreen';
import Blank from 'lib/Blank';
import useCompanyId from './useCompanyId';
import useAreaState from 'comps/City/AreaTools/useContextState';
import useAreaCommands from 'comps/City/AreaTools/useCommands';
import useFilters from './useFilters';
import useHasLocation from './useHasLocation';
import useContextState from './useContextState';
import useCommands from './useCommands';
import * as log from 'lib/log';
import { Tooltip } from 'react-bootstrap';
import OverlayTriggerWrapper from 'comps/OverlayTriggerWrapper';
import cx from 'classnames';
import type { SerializedStyles } from '@emotion/utils';
import useHasFilters from './useHasFilters';
import LTAssociatedView from './LTAssociatedView';
import scrollContainerTop from './scrollContainerTop';
import LTListView, {
  LTListViewSkeletonListItem,
  LTListViewItem,
} from './LTListView';
import truncate from 'underscore.string/truncate';
import useStoreState from 'lib/useStoreState';
import PATHS from 'const/paths';
import { ltButtonBarBg } from "./colors";
import MakeVacant from 'comps/ResourceBuilder/ResourceForm/MakeVacant';

const logError = throttle(log.error);

const enabledResourcesForAssociatedView = [
  'Building',
  'Development',
  'Event',
  'Organization',
  'Listing',
  'Property',
  'Tenant',
  'Unit',
];

function useInitKeywordInput() {
  const s = useStoreState({
    started: '$queryInitialized',
    f: '$query.filter.q',
  });
  return s.started && s.f && s.f ? s.f : '';
}

let searchKeywordInputBackup = '';
function useBackupSeachInput() {
  const cmd = useCommands();
  const { searchKeywordInput } = useContextState();
  const keywordInput = useInitKeywordInput();

  useEffect(() => {
    return () => {
      searchKeywordInputBackup = searchKeywordInput;
    };
  }, [searchKeywordInput]);

  useEffect(() => {
    cmd.set(['searchKeywordInput'], keywordInput || searchKeywordInputBackup);
  }, []);
}

function closePopup() {
  const map = window.cityMap || window.districtMap;
  if (!map) return;
  map.closePopup();
}

function LTLocationHeaderSearchField() {
  const cmd = useCommands();
  const { searchKeywordInput } = useContextState();
  const filters = useFilters();
  const isSearchRun = filters.get('q') === searchKeywordInput;
  useBackupSeachInput();

  const onClear = () => {
    cmd.set(['searchKeywordInput'], '');
    cmd.setFilterQuery('');
  };

  function onKeyUp(e) {
    // Update the filter when the user hits the enter key
    if (e.which === 13 || e.key === 'Enter') {
      runSearch();
    }
  }

  function runSearch() {
    cmd.setFilterQuery(searchKeywordInput);
  }

  function onClearOrRun() {
    if (isSearchRun) {
      onClear();
    } else {
      runSearch();
    }
  }

  function onChange(value) {
    cmd.set(['searchKeywordInput'], value);
  }

  return (
    <div
      data-testid="LTLocationHeaderSearchField"
      css={css(`
        padding: 2rem 3rem 0 3rem;
      `)}
    >
      <div
        css={css(`
        border: 1px solid rgba(23, 121, 210, 0.6);
        border-radius: 8px;
        padding-left: 1rem;
        padding-top: 0.5rem;
        padding-bottom: 0.35rem;
      `)}
        className="d-flex align-items-center"
      >
        <i
          className="fa fa-search text-muted"
          css={css(`
          margin-right: 2rem;
         `)}
        />
        <input
          data-testid="LTSearchBodyKeywordInput"
          type="text"
          placeholder="Search keyword"
          value={searchKeywordInput}
          onChange={(e) => onChange(e.target.value)}
          onKeyUp={onKeyUp}
          className="form-control border-0"
          css={css(`background-color: white;`)}
        />
        {searchKeywordInput && (
          <LTLocationHeaderSearchFieldClearButton
            isSearchRun={isSearchRun}
            onClick={onClearOrRun}
          />
        )}
      </div>
    </div>
  );
}

function LTLocationHeaderSearchFieldClearButton({ onClick, isSearchRun }) {
  return (
    <a
      data-testid="LTLocationHeaderSearchFieldClearButton"
      onClick={onClick}
      className="mr-4"
    >
      <i
        className={cx('fas text-muted', {
          'fa-times': isSearchRun,
          'fa-arrow-alt-circle-right': !isSearchRun,
        })}
      />
    </a>
  );
}

function useNewResource() {
  const { focusedLatlon } = useContextState();
  const cmdBuilder = useBuilderCommands();
  const isActive = Boolean(focusedLatlon);

  function onClick() {
    if (!focusedLatlon) return;
    const theGeom = mkPoint(focusedLatlon);
    cmdBuilder.open();
    cmdBuilder.setResourceKey('theGeom', theGeom);
  }
  return { isActive, onClick };
}

function LTNLHTEButtonsNewRecordButton() {
  const { isActive, onClick } = useNewResource();

  return (
    <button
      onClick={onClick}
      css={css(`
          border-radius: 69px;
          text-transform: uppercase;
          padding: 0.75rem 1.5rem;
          font-weight: 700;
          font-size: 1.2rem;
          ${
            isActive
              ? `
              color: white;
              background-color: var(--primary2);
              &:hover {
                background-color: rgba(23, 121, 210, 0.6);
              }`
              : 'border: solid 1px #ccc;'
          }
        `)}
      className={cx({ 'newResourceBtn text-muted-2': !isActive })}
    >
      <i className="fa fa-plus" /> Add a record
    </button>
  );
}

function ActiveFilterIcon() {
  const hasFilters = useHasFilters();
  return (
    <span data-testid="ActiveFilterIcon" className="position-relative mr-2">
      <i className={cx('fa fa-sliders-h', { 'green-dot': hasFilters })} />
    </span>
  );
}

function LTNLHTEButtonsFilterButton() {
  const cmd = useCommands();
  function onClick() {
    cmd.set(['activePanel'], 'FILTERS');
  }
  return (
    <button
      onClick={onClick}
      css={css(`
        border: solid 1px #ccc;
        border-radius: 69px;
        text-transform: uppercase;
        padding: 0.75rem 1.5rem;
        color: #626262;
        font-weight: 700;
        font-size: 1.2rem;

        ${isSmallScreen() ? '' : 'margin-right: 2rem;'}

        &:hover {
          color: #1779D2;
          border-color: #1779D2;
        }
      `)}
    >
      <ActiveFilterIcon />
      Filter Map
    </button>
  );
}

function LTNLHTEButtonsCollapseButton() {
  const cmd = useCommands();
  function onClick() {
    cmd.set(['locHeaderToolsOpen'], false);
  }
  return (
    <IconButton
      id="collapse-tools"
      tooltipCopy="Collapse"
      iconClass="fa-angle-up text-muted"
      onClick={onClick}
      className="p-0"
    />
  );
}

function useMapName() {
  const s = useAreaState();
  if (!s) return '';
  const { areaWorkingCopy } = s;
  return areaWorkingCopy.get('name');
}

function LTNLHTEMapArea() {
  const areaCmd = useAreaCommands();
  const name = useMapName();

  function onClick() {
    areaCmd.clearArea();
  }

  return (
    <div data-testid="LTNLHTEMapArea" css={css('margin-bottom: 2rem;')}>
      <div css={css('font-size: 1.2rem; color: #636363; font-weight: bold;')}>
        VIEWING
      </div>
      {name ? (
        <div
          className={cx('d-flex justify-content-between')}
          css={css('font-size: 2rem; color: #333; padding-right: 0.5rem;')}
        >
          {name}
          <button onClick={onClick} className="mb-0 p-0">
            <i className="fal fa-times-circle" />
          </button>
        </div>
      ) : (
        <div className="d-flex align-items-center mb-0 p-0 w-100 text-muted-2 font-weight-bold">
          No map area selected
        </div>
      )}
    </div>
  );
}
function LTNLHTEButtons() {
  return (
    <div
      data-testid="LTNLHTEButtons"
      className="d-flex justify-content-between align-items-center pr-1"
    >
      <div>
        <LTNLHTEButtonsFilterButton />
        <LTNLHTEButtonsNewRecordButton />
      </div>
      <LTNLHTEButtonsCollapseButton />
    </div>
  );
}

function useRemoveLocation() {
  const cmd = useCommands();

  return function() {
    cmd.set(['focusedLatlon'], null);
    cmd.set(['searchAddressInput'], '');
    cmd.removeMarker();
  };
}

function LTNLHTEToolsLocation() {
  const { focusedLatlon, searchAddressInput } = useContextState();
  const onRemoveLocation = useRemoveLocation();

  if (!useHasLocation()) return <div />;

  let text = truncate(searchAddressInput || '', 100);

  // When there is an address show the address. Otherwise, show latlon.
  if (focusedLatlon && !text) {
    const { lat, lon } = focusedLatlon;
    text = (
      <Fragment>
        <div
          css={css(`
          color: #636363;
          font-size: 1.2rem;
          line-height: 2.2rem;
          max-width: 205px;
          text-overflow: ellipsis;
          overflow: hidden;
        `)}
        >
          {lon}, {lat}
        </div>
      </Fragment>
    );
  }

  return (
    <div
      data-testid="LTNLHTEToolsLocation"
      css={css(`
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding-right: 0.5rem;
      `)}
    >
      <div
        css={css(`
        width: 87%;
        display: flex;
      `)}
      >
        <div className="pr-3 text-muted-2" onClick={onRemoveLocation}>
          <i className={cx('fa fa-map-marker-alt')} />
        </div>
        <div
          css={css(`
            color: #333;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 285px;
            font-size: 1.2rem;
            line-height: 2.2rem;
          `)}
        >
          {text}
        </div>
      </div>
      <button
        className="p-0 text-danger d-block"
        onClick={onRemoveLocation}
        css={css('font-size: 1.2rem;')}
      >
        Clear
      </button>
    </div>
  );
}

function LTNLHTETools() {
  const hasLocation = useHasLocation();
  if (!hasLocation) return null;
  return (
    <div date-testid="LTNLHTETools" className="mb-4">
      <LTNLHTEToolsLocation />
    </div>
  );
}

// Alias: LTNLHTE
function LTLocationHeaderToolsExpanded() {
  const hasLocation = useHasLocation();

  return (
    <div
      data-testid="LTLocationHeaderToolsExpanded"
      className={cx({ 'mt-3': hasLocation, 'mt-2dot5': !hasLocation })}
    >
      <LTNLHTETools />
      <LTNLHTEMapArea />
      <LTNLHTEButtons />
    </div>
  );
}

function LTNLHTCButtonsLocationButton() {
  const onRemoveLocation = useRemoveLocation();
  if (!useHasLocation()) return null;

  return (
    <IconButton
      id="location-marker"
      tooltipCopy="Clear location"
      cssString={css(`
        padding: 0 1rem 0rem 1rem;
        color: #626262;
      `)}
      iconClass="fa-map-marker-times text-danger"
      onClick={onRemoveLocation}
    />
  );
}

function LTNLHTCButtonsFilterButton() {
  const cmd = useCommands();
  function onClick() {
    cmd.set(['activePanel'], 'FILTERS');
  }
  const hasFilters = useHasFilters();
  return (
    <IconButton
      id="filter-map"
      tooltipCopy="Filter Map"
      onClick={onClick}
      iconClass={cx('fa-sliders-h', { 'green-dot': hasFilters })}
      cssString={css(`
        padding: 0 0rem 0rem 1rem;
        margin-right: 1rem;
        color: #626262;
        position: relative;
      `)}
    />
  );
}

function IconButton({
  onClick,
  iconClass,
  className,
  cssString,
  tooltipCopy,
  id,
}: {
  onClick: () => any,
  iconClass: string,
  className?: string,
  cssString?: SerializedStyles,
  tooltipCopy: string,
  id: string,
}) {
  return (
    <OverlayTriggerWrapper
      placement="left"
      trigger={['hover']}
      overlay={<Tooltip id={id}>{tooltipCopy}</Tooltip>}
    >
      <button id={id} className={className} css={cssString} onClick={onClick}>
        <i className={cx('fa', iconClass)} />
      </button>
    </OverlayTriggerWrapper>
  );
}

function LTNLHTCButtonsNewRecordButton() {
  const { isActive, onClick } = useNewResource();
  return (
    <IconButton
      id="add-record"
      tooltipCopy="Add a record"
      className={cx('pl-3 pr-3', { 'text-muted-2': !isActive })}
      cssString={css(`
        ${
          isActive
            ? `
          color: var(--primary2);
        `
            : ''
        }
      `)}
      onClick={onClick}
      iconClass="fa-plus"
    />
  );
}

function LTNLHTCButtonsExpandButton() {
  const cmd = useCommands();
  function onClick() {
    cmd.set(['locHeaderToolsOpen'], true);
  }

  return (
    <IconButton
      id="expand-tools"
      tooltipCopy="Expand"
      className="border-left pl-3 pr-3"
      onClick={onClick}
      iconClass="fa-angle-down text-muted"
    />
  );
}

function LTNLHTCButtons() {
  return (
    <div
      data-testid="LTNLHTCButtons"
      className="d-flex justify-content-end align-items-center"
    >
      <LTNLHTCButtonsLocationButton />
      <LTNLHTCButtonsFilterButton />
      <LTNLHTCButtonsNewRecordButton />
      <LTNLHTCButtonsExpandButton />
    </div>
  );
}

function LTNLHTCMapArea() {
  const name = useMapName();

  return (
    <div
      data-testid="LTNLHTCMapArea"
      className={cx({ 'text-muted-2': !name })}
      css={css('user-select: none;')}
    >
      {name || 'No map area selected'}
    </div>
  );
}

// Alias: LTNLHTC
function LTLocationHeaderToolsCollapsed() {
  return (
    <div
      className="d-flex justify-content-between mt-3"
      data-testid="LTLocationHeaderToolsCollapsed"
    >
      <LTNLHTCMapArea />
      <LTNLHTCButtons />
    </div>
  );
}

function LTLocationHeaderToolsEmbedded() {
  return (
    <div data-testid="LTLocationHeaderToolsEmbedded" className="pl-5 pr-5 mt-3">
      <LTNLHTEToolsLocation />
    </div>
  );
}

function LTLocationHeaderTools() {
  const { locHeaderToolsOpen } = useContextState();
  // Hide this section on embedded maps.
  if (!isPath('/map')) return <LTLocationHeaderToolsEmbedded />;

  return (
    <div data-testid="LTLocationHeaderTools" className="pl-5 pr-5">
      {createElement(
        locHeaderToolsOpen
          ? LTLocationHeaderToolsExpanded
          : LTLocationHeaderToolsCollapsed
      )}
    </div>
  );
}

function LTLocationHeader() {
  return (
    <div
      data-testid="LTLocationHeader"
      css={css('box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);')}
      className="pb-3"
    >
      <LTLocationHeaderSearchField />
      <LTLocationHeaderTools />
    </div>
  );
}

function LTDetailViewBodyInnerTableFields({ type, value }) {
  const NoFieldset = Struct.clone({
    Fieldset: ({ children }) => {
      return children;
    },
  });
  const noFieldset = {
    legend: '',
    template: NoFieldset,
  };
  const t2 = type.opt(noFieldset);
  if (value && !value.hasAdjusted) {
    if (value.leaseStart) {
      let leaseStartDate = new Date(value.leaseStart);
      leaseStartDate.setUTCMonth(leaseStartDate.getUTCMonth() - 1);
      value.leaseStart = leaseStartDate.toISOString();
    }
    if (value.leaseExpiration) {
      let leaseExpirationDate = new Date(value.leaseExpiration);
      leaseExpirationDate.setUTCMonth(leaseExpirationDate.getUTCMonth() - 1);
      value.leaseExpiration = leaseExpirationDate.toISOString();
    }
    if (value.businessOpenedOn) {
      let businessOpenedOnDate = new Date(value.businessOpenedOn);
      businessOpenedOnDate.setUTCMonth(businessOpenedOnDate.getUTCMonth() - 1);
      value.businessOpenedOn = businessOpenedOnDate.toISOString();
    }
    if (value.businessClosedOn) {
      let businessClosedOnDate = new Date(value.businessClosedOn);
      businessClosedOnDate.setUTCMonth(businessClosedOnDate.getUTCMonth() - 1);
      value.businessClosedOn = businessClosedOnDate.toISOString();
    }
    value.hasAdjusted = true;
  }
  return <Fields type={t2} value={value} />;
}

function LTDetailViewBodyInnerTools({ visibility, status }) {
  const s = useContextState();
  const focusedResourceId = s.focusedResourceId;
  const focusedResourceType = s.focusedResourceType;
  const cmdBuilder = useBuilderCommands();

  function onEdit() {
    if (!focusedResourceId) return;
    if (!focusedResourceType) return;

    cmdBuilder.editResource({
      id: focusedResourceId,
      resourceName: focusedResourceType,
    });
  }

  function onDup() {
    if (!focusedResourceId) return;
    if (!focusedResourceType) return;

    cmdBuilder.dupResource({
      id: focusedResourceId,
      resourceName: focusedResourceType,
    });
  }

  if (!isPath('/map')) return null;

  return (
    <div
      data-testid="LTDetailViewBodyInnerTools"
      css={css(`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
          `)}
    >
      <div className='d-flex'>
        <button className="text-link editResourceBtn pl-0" onClick={onEdit}>
          <i className="fa fa-edit" /> Edit
        </button>{' '}
        <button className="text-link" onClick={onDup}>
          <i className="fa fa-clone" /> Duplicate
        </button>

       { focusedResourceType === 'Unit' && status === 'Occupied' ?
       (
        < MakeVacant id={focusedResourceId} resourceName={focusedResourceType}/>
      )
       : null }

      </div>
      <div className="text-muted">
        <OverlayTriggerWrapper
          placement="top"
          trigger={['hover']}
          overlay={
            <Tooltip id="visibility-tooltip">
              {visibility === 'Public' ? 'Visible' : 'Not visible'} on your
              shared maps
            </Tooltip>
          }
        >
          <span className="cursor-pointer" id="visibility-tooltip">
            {visibility}
          </span>
        </OverlayTriggerWrapper>
      </div>
    </div>
  );
}

function LTDetailView() {
  const { projectRefetchHash } = useStoreState({
    projectRefetchHash: PATHS.projectRefetchHash,
  });

  const [projectRefreshed, setProjectRefreshed] = useState(
    projectRefetchHash
  );
  const s = useContextState();
  const focusedResourceId = s.focusedResourceId;
  const focusedResourceType = s.focusedResourceType;
  const focusedResourceColor = s.focusedResourceColor;
  const cmd = useCommands();
  const resourceName = focusedResourceType;
  const id = focusedResourceId;
  const [_, setTick] = useReducer((v) => v + 1, 0);
  const c1 = useCompanyId();
  const companyId = window.assetCompanyId || c1;
  const variables = { id, resourceName, companyId };
  const skip = Blank.is(companyId);
  const opts = { variables, skip };
  const { data, loading, error, refetch } = useCachedQuery(GetItemViewResource, opts);

  // Try until companyId or assetCompanyId is found.
  useEffect(() => {
    if (companyId) return;
    setTick();
  }, [companyId]);

  useEffect(() => {
    scrollContainerTop();
  }, [id]);

  useEffect(() => {
    if (Present.is(error)) log.error(error);
  }, [error]);

  if (loading || Blank.is(data)) return <LTDetailViewSkeleton />;

  if (error) {
    return (
      <div className="text-danger p-5" style={{ wordBreak: 'break-all' }}>
        {JSON.stringify(error)}
      </div>
    );
  }

  // Refetch the data when the record is updated
  if (
    projectRefetchHash !== undefined &&
    projectRefreshed !== projectRefetchHash
  ) {
    refetch();
  }

  const payload = (I: any).getIn(data, ['company', 'resource']);

  if (!payload) {
    logError(`Empty payload: ${JSON.stringify(data)}`);
    return <div>No data sent.</div>;
  }

  if (!focusedResourceType) {
    return null;
  }

  function onGoBack() {
    cmd.set(['focusedResourceId'], null);
    cmd.set(['focusedResourceType'], null);
    cmd.set(['focusedResourceColor'], null);
    if (isEmbedded()) {
      cmd.set(['focusedLatlon'], null);
    }

    scrollContainerTop();

    closePopup();
  }
  const projectType = getTypeConfig(focusedResourceType);
  const showAssociatedView = enabledResourcesForAssociatedView.includes(
    focusedResourceType
  );

  function onOpenNewPage() {
    const id2 = payload.hashid;
    window.open(`${APP_BASE}/page/${id2}`);
  }

  return (
    <div data-testid="LTDetailView" key={payload.hashid}>
      <button
        data-testid="LTDetailViewNav"
        css={css(`
          padding: 2.2rem 1rem 1rem 3rem;
        `)}
        className="text-link"
        onClick={onGoBack}
      >
        Back to list
      </button>
      <div
        data-testid="LTDetailViewHeader"
        css={css(`
          padding: 0 0 0 2rem;
          & > div > div {
            padding-bottom: 2rem;
            padding-left: 1rem;
          }
        `)}
      >
        <LTListViewItem
          {...payload}
          type={focusedResourceType}
          fillColor={focusedResourceColor}
          notPartOfList
        />
      </div>
      <div
        data-testid="LTDetailViewBody"
        css={css(`
          padding: 0 0 0 3.2rem;
        `)}
      >
        <div
          data-testid="LTDetailViewBodyInner"
          css={css(`
            border-left: solid 5px ${focusedResourceColor || 'black'};
            padding-left: 2.3rem;
            padding-right: 3rem;
            // padding-bottom: 2rem;
          `)}
        >
          <LTDetailViewBodyInnerTools visibility={payload.visibility} status= {payload.status} />
          <div
            data-testid="LTDetailViewBodyInnerTable"
            css={css(`
            .table-row {
              margin-bottom: 2rem;
              & > div:nth-of-type(1) {
                text-transform: uppercase;
                color: #626262;
                margin-bottom: 0.2rem;
                font-size: 1.2rem;
              }
            }
            .row-without-label {
              margin-bottom: 2rem;
              img {
                max-width: 100%;
              }
            }
          `)}
          >
            <LTDetailViewBodyInnerTableFields
              type={projectType}
              value={payload}
            />
            <button onClick={onOpenNewPage} className="text-link mt-3 p-0">
              Open as new page
            </button>
          </div>

          {showAssociatedView ? (
            <LTAssociatedView />
          ) : (
            <div css={css('height: 2rem;')} />
          )}
        </div>
      </div>
    </div>
  );
}

function LTDetailViewSkeletonBodyRow() {
  return (
    <div
      data-testid="LTDetailViewSkeletonBodyRow"
      css={css('margin-bottom: 2rem;')}
    >
      <div
        css={css(`
          background: #efefef;
          height: 1.2rem;
          width: 80px;
          margin-bottom: 0.2rem;
        `)}
      />
      <div
        css={css(`
          background: #efefef;
          height: 1rem;
          width: 80%;
          margin-bottom: 0.2rem;
        `)}
      />
    </div>
  );
}

function LTDetailViewSkeleton() {
  return (
    <div data-testid="LTDetailViewSkeleton">
      <div
        data-testid="LTDetailViewSkeletonBackButton"
        css={css(`
          margin: 2.2rem 3rem 1rem 3rem;
          height: 1.2rem;
          width: 130px;
          background: #efefef;
        `)}
      />
      <div
        data-testid="LTDetailViewSkeletonListItem"
        css={css(`
          padding-left: 0rem;
          & > div  { padding-bottom: 2rem; }
        `)}
      >
        <LTListViewSkeletonListItem />
      </div>
      <div
        data-testid="LTDetailViewSkeletonBody"
        css={css(`
            border-left: solid 5px #efefef;
            margin-left: 3.3rem;
            padding-left: 2.3rem;
            padding-right: 3rem;
            min-height: 400px;
          `)}
      >
        {I.Range(0, 10).map((i) => (
          <LTDetailViewSkeletonBodyRow key={i} />
        ))}
      </div>
    </div>
  );
}

function useDetailView() {
  const s = useContextState();
  return (
    Boolean(s.focusedResourceType) &&
    Boolean(s.focusedResourceColor) &&
    Boolean(s.focusedResourceId)
  );
}

export default function LTLocation(): Element<string> {
  const showDetailView = useDetailView();

  return (
    <div
      data-testid="LTLocation"
      css={css(`
        background-color: white;
        color: #333;
        display: grid;
        grid-template-rows: auto 1fr;
        min-height: 100%;
      `)}
    >
      {showDetailView ? null : <LTLocationHeader />}
      {showDetailView ? <LTDetailView /> : <LTListView />}
    </div>
  );
}
